import React, { useCallback, useState } from 'react';
import { Paper, Button, makeStyles, IconButton, FormControl, FormLabel, FormHelperText, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompProps, BaseInfoCompSaveAction, FontIcon, ModeInfoCompTextInputField, useModeFormStyle,
    useModePanelStyle, usePropValue } from '@moderepo/mode-app-components';
import { FormatJSONAction } from '../../componentUtils';



const useStyle = makeStyles((theme: Theme) => {
    return {

        panelHeaderIcon: {
        },

        textArea: {
            width     : '100%',
            resize    : 'vertical',
            minHeight : 500,
            overflow  : 'scroll',
            whiteSpace: 'nowrap',
            marginTop : theme.spacing(0.5),
            padding   : theme.spacing(1),
            border    : `1px solid ${theme.palette.divider}`,
            background: 'none',
            color     : theme.palette.text.primary,
        },

    };
}, { name: 'HomeInfo', index: 1 });


export interface RCGatewayDefinitionInfoActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate?: BaseInfoCompSaveAction<{
        gatewayDef: string | undefined
    }>;
    readonly formatJson?: FormatJSONAction;
}

export interface RCGatewayDefinitionInfoProps extends BaseInfoCompProps {
    readonly fields: {
        readonly gatewayDef?: BaseEntityField<string>;
        readonly creationTime?: BaseEntityField<string>;
    }
    readonly actions: RCGatewayDefinitionInfoActionsSet;
}


/**
 * This is a DUMB component used for displaying home info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCGatewayDefinitionInfo: React.FC<RCGatewayDefinitionInfoProps> = (props: RCGatewayDefinitionInfoProps) => {
    const [gatewayDefInput, setGatewayDefInput] = usePropValue<string | undefined>(props.fields.gatewayDef?.value);

    const [jsonError, setJsonError] = useState<string>();

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setGatewayDefInput(props.fields.gatewayDef?.value);
    }, [props, setGatewayDefInput]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                gatewayDef: gatewayDefInput?.trim(),
            });
        }

        return false;
    }, [props, gatewayDefInput]);


    const onFormatJSON = useCallback(() => {
        if (gatewayDefInput && props.actions.formatJson) {
            try {
                setGatewayDefInput(props.actions.formatJson.onClick(gatewayDefInput));
            } catch (error) {
                setJsonError((error as Error).message);
            }
        }
    }, [gatewayDefInput, props.actions.formatJson, setGatewayDefInput]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <div className={clsx(panelClasses.panelHeader)}>
                <div className={clsx(panelClasses.panelHeaderIconContainer)}>
                    {props.icon && <FontIcon className={clsx(panelClasses.panelHeaderIcon, classes.panelHeaderIcon)} iconName={props.icon} />}
                </div>
                <div className={panelClasses.panelHeaderTitle}>
                    {props.title}
                </div>
                {props.actions?.startEdit && !props.isEditing && (
                    <IconButton onClick={() => {
                        if (props.actions?.startEdit) {
                            props.actions.startEdit.onClick();
                        }
                    }}
                    >
                        <FontIcon iconName={props.actions.startEdit.icon || 'edit'} />
                    </IconButton>
                )}
                {props.actions?.removeObject && !props.isEditing && (
                    <IconButton
                        color="secondary"
                        onClick={() => {
                            if (props.actions?.removeObject) {
                                props.actions.removeObject.onClick();
                            }
                        }}
                    >
                        <FontIcon iconName={props.actions.removeObject.icon || 'delete_outline'} />
                    </IconButton>
                )}
            </div>
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={clsx(formClasses.formInputGroup)}>
                        {props.fields.gatewayDef && (
                            <FormControl
                                className={clsx(`${formClasses.formInputField}`,
                                    props.isEditing && !props.fields.gatewayDef.editable ? 'fade' : '')}
                                required={props.isEditing && props.fields.gatewayDef.required}
                                error={Boolean(jsonError || props.fields.gatewayDef.error)}
                            >
                                <FormLabel
                                    component="label"
                                    className={clsx('checkbox-label', props.fields.gatewayDef.editable ? 'editable-label-icon' : '')}
                                >{props.fields.gatewayDef.label}
                                </FormLabel>
                                <textarea
                                    className={clsx(classes.textArea,
                                        !props.fields.gatewayDef.editable ? 'readonly' : '',
                                        !props.isEditing && props.fields.gatewayDef.clickable && 'clickable')}
                                    required={props.isEditing && props.fields.gatewayDef.required}
                                    value={props.fields.gatewayDef.editable
                                        ? gatewayDefInput : props.fields.gatewayDef.displayValue || props.fields.gatewayDef.value}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                                        if (props.fields.gatewayDef?.editable) {
                                            const { value } = event.currentTarget as HTMLTextAreaElement;
                                            setGatewayDefInput(value);
                                            setJsonError(undefined);
                                            if (props.onFormInputChange) {
                                                props.onFormInputChange('gatewayDef', value);
                                            }
                                        }
                                    }}
                                    onClick={() => {
                                        if ((props.fields.gatewayDef?.editable || props.fields.gatewayDef?.clickable)
                                            && props.onFieldClicked && !props.isEditing) {
                                            props.onFieldClicked('gatewayDef');
                                        }
                                    }}
                                />
                                <FormHelperText>
                                    {jsonError || props.fields.gatewayDef.error || props.fields.gatewayDef.description || ''}
                                </FormHelperText>
                            </FormControl>
                        )}
                        {props.fields.creationTime && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.creationTime}
                                fieldName="creationTime"
                                inputValue={props.fields.creationTime.displayValue ?? props.fields.creationTime.value}
                            />
                        )}
                    </div>
                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.formatJson && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        color="primary"
                                        size="small"
                                        startIcon={props.actions.formatJson.icon
                                            ? <FontIcon iconName={props.actions.formatJson.icon} /> : undefined}
                                        onClick={onFormatJSON}
                                    >
                                        {props.actions.formatJson.label}
                                    </Button>
                                )}
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            setJsonError(undefined);
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );

};
